// Supabase data-plane client — the counterpart of firebaseConfig.js for the
// `REACT_APP_DATA_PLANE=supabase` plane (self-hosted Supabase: GoTrue auth +
// Postgres + postgres_changes realtime).
//
// Doc-table model (contract with the backend): each Firestore collection maps
// to a Postgres table `id text pk, data jsonb, created_at, updated_at`; child
// collections flatten with parent-id columns (see data/pathMap.js). Rows are
// surfaced to the app as `{ id, ...data }` — identical to Firestore doc shapes.
//
// When REACT_APP_DATA_PLANE is unset (default 'firebase') nothing here runs.
import { createClient } from '@supabase/supabase-js';
import { kitEnv, SITE_MODE } from './kitEnv.js';

export const DATA_PLANE = kitEnv('DATA_PLANE', process.env.REACT_APP_DATA_PLANE || 'firebase');
export const isPg = () => DATA_PLANE === 'supabase';

let client = null;
let warned = false;

// Lazy singleton. Returns null (with a one-time console.warn) when the env
// vars are missing, so the dev server doesn't crash before Supabase is wired.
// Site mode (static snapshot, stack stopped) also returns null: every
// subscribe helper below no-ops on a null client and auth reports signed out,
// so nothing retries against an origin with no API behind it.
export function getSupabase() {
  if (client) return client;
  if (SITE_MODE) return null;
  // Runtime (env.js) first, build-time env second, same-origin last — the
  // prebuilt bundle serves any instance from its own domain.
  const url = kitEnv('SUPABASE_URL', process.env.REACT_APP_SUPABASE_URL
    || (typeof window !== 'undefined' ? window.location.origin : ''));
  const anonKey = kitEnv('SUPABASE_ANON_KEY', process.env.REACT_APP_SUPABASE_ANON_KEY);
  if (!url || !anonKey) {
    if (!warned) {
      warned = true;
      // eslint-disable-next-line no-console
      console.warn(
        '[supabaseClient] REACT_APP_DATA_PLANE=supabase but REACT_APP_SUPABASE_URL / ' +
          'REACT_APP_SUPABASE_ANON_KEY are not set — data plane disabled.'
      );
    }
    return null;
  }
  client = createClient(url, anonKey);
  return client;
}

// ---- Live row subscriptions ------------------------------------------------
// Firestore-onSnapshot equivalents over `postgres_changes`: initial fetch,
// then maintain the row set from INSERT/UPDATE/DELETE events. Docs emitted as
// `{ id, ...data }`, ordering/limit applied client-side (doc payloads are
// small in this kit; server-side `.order('data->>x')` buys nothing here).

let channelSeq = 0;
const toDoc = (row) => ({ id: row.id, ...(row.data || {}) });

function compareBy(orderBy, dir) {
  return (a, b) => {
    const av = a[orderBy];
    const bv = b[orderBy];
    if (av === bv) return 0;
    if (av == null) return 1; // missing values last, either direction
    if (bv == null) return -1;
    const r = av > bv ? 1 : -1;
    return dir === 'desc' ? -r : r;
  };
}

// Subscribe to a table slice. `filters` are server-side equality filters on
// real columns (the flattened parent-id cols); only the FIRST one can ride the
// realtime filter, the rest (plus the optional `match(doc)` predicate, for
// values living inside `data`) are re-checked client-side on every event.
// Returns an unsubscribe function. onRows receives the full sorted array.
export function subscribeRows(table, filters = [], opts = {}, onRows, onError) {
  const { orderBy, dir = 'desc', limit, match } = opts;
  const supabase = getSupabase();
  if (!supabase) {
    onRows([]);
    return () => {};
  }
  let disposed = false;
  const rows = new Map(); // id -> doc

  const emit = () => {
    let arr = Array.from(rows.values());
    if (orderBy) arr.sort(compareBy(orderBy, dir));
    if (limit) arr = arr.slice(0, limit);
    onRows(arr);
  };
  const accepts = (row, doc) =>
    filters.every((f) => row[f.col] === f.value) && (!match || match(doc));

  const refetch = () => {
    let query = supabase.from(table).select('id,data');
    for (const f of filters) query = query.eq(f.col, f.value);
    return query.then(({ data, error }) => {
      if (disposed) return;
      if (error) {
        if (onError) onError(error.message);
        emit();
        return;
      }
      rows.clear();
      for (const row of data || []) {
        const doc = toDoc(row);
        if (!match || match(doc)) rows.set(row.id, doc);
      }
      emit();
    });
  };
  refetch();
  // Realtime can silently fail to deliver (table missing from the
  // publication, RLS on the realtime role, websocket blocked) — the list
  // then looks frozen after the user's own write. Fall back to a slow poll
  // whenever the channel is not confirmed SUBSCRIBED.
  let poll = null;
  const setPolling = (on) => {
    if (on && !poll) poll = setInterval(refetch, 15000);
    if (!on && poll) { clearInterval(poll); poll = null; }
  };

  const params = { event: '*', schema: 'public', table };
  if (filters.length) params.filter = `${filters[0].col}=eq.${filters[0].value}`;
  const channel = supabase
    .channel(`rows:${table}:${(channelSeq += 1)}`)
    .on('postgres_changes', params, (payload) => {
      if (disposed) return;
      if (payload.eventType === 'DELETE') {
        if (payload.old?.id && rows.delete(payload.old.id)) emit();
        return;
      }
      const row = payload.new;
      if (!row?.id) return;
      const doc = toDoc(row);
      if (accepts(row, doc)) {
        rows.set(row.id, doc);
        emit();
      } else if (rows.delete(row.id)) {
        emit(); // row updated out of this slice
      }
    });
  if (filters.length) {
    // DELETE payloads only carry the primary key (replica identity), so a
    // column-filtered subscription can miss them — listen unfiltered too and
    // drop by id (no-op for rows we never held).
    channel.on('postgres_changes', { event: 'DELETE', schema: 'public', table }, (payload) => {
      if (!disposed && payload.old?.id && rows.delete(payload.old.id)) emit();
    });
  }
  channel.subscribe((status) => { if (!disposed) setPolling(status !== 'SUBSCRIBED'); });

  return () => {
    disposed = true;
    setPolling(false);
    supabase.removeChannel(channel);
  };
}

// Subscribe to a single row by id. onDoc receives `{ id, ...data }` | null.
// `filters` = the parent columns from resolvePath — child rows are keyed
// (parents…, id), so a seat doc (id = uid) exists once per workspace.
export function subscribeRow(table, id, onDoc, onError, filters = []) {
  const supabase = getSupabase();
  if (!supabase) {
    onDoc(null);
    return () => {};
  }
  let disposed = false;

  const refetch = () => supabase
    .from(table)
    .select('id,data')
    .match(Object.fromEntries([['id', id], ...filters.map((f) => [f.col, f.value])]))
    .maybeSingle()
    .then(({ data, error }) => {
      if (disposed) return;
      if (error) {
        if (onError) onError(error.message);
        return;
      }
      onDoc(data ? toDoc(data) : null);
    });
  refetch();
  // Same degradation as subscribeRows: when the channel is not confirmed
  // SUBSCRIBED (realtime 403 behind a stale proxy config, websocket
  // blocked), poll instead of freezing on the first fetch — a Dev Console
  // session otherwise shows "waiting for the runner" long after the row
  // flipped to error.
  let poll = null;
  const setPolling = (on) => {
    if (on && !poll) poll = setInterval(refetch, 10000);
    if (!on && poll) { clearInterval(poll); poll = null; }
  };

  const channel = supabase
    .channel(`row:${table}:${id}:${(channelSeq += 1)}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table, filter: `id=eq.${id}` },
      (payload) => {
        if (disposed) return;
        const row = payload.eventType === 'DELETE' ? payload.old : payload.new;
        if (filters.some((f) => row && row[f.col] !== undefined && String(row[f.col]) !== String(f.value))) return;
        if (payload.eventType === 'DELETE') onDoc(null);
        else if (payload.new?.id) onDoc(toDoc(payload.new));
      }
    )
    .subscribe((status) => { if (!disposed) setPolling(status !== 'SUBSCRIBED'); });

  return () => {
    disposed = true;
    setPolling(false);
    supabase.removeChannel(channel);
  };
}
